import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingCart, ChevronDown, Gift, BookOpen, Wine, MapPin, Users } from "lucide-react";
import client, { formatApiError } from "../lib/api";
import { trackAmazonClick, trackEvent } from "../lib/tracking";
import SEO from "../components/SEO";
import Divider from "../components/Divider";
import BookMockup from "../components/BookMockup";
import TestimonialSlider from "../components/TestimonialSlider";
import { Reveal, MaskedLines } from "../components/Reveal";

const IMGS = {
  pour: "https://images.pexels.com/photos/24748876/pexels-photo-24748876.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  bottle: "https://images.unsplash.com/photo-1695048475729-f82a29f68e72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHxfMTc4NzI2MDA3Nnw&q=85",
  portrait: "/assets/stefano.jpg",
  vineyard: "https://images.unsplash.com/photo-1585553616435-2dc0a54e271d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHxfMTc4NzI2MDA2Nnw&q=85",
};

const CONTENTS = [
  "Mappe regionali commentate: dove nasce ogni grande vino italiano",
  "I vitigni autoctoni che devi conoscere, regione per regione",
  "Denominazioni, DOC e DOCG spiegate senza burocratese",
  "Aneddoti veri dalle cantine, raccolti in 30 anni di visite",
];

const FAQS = [
  { q: "In che formato è disponibile il libro?", a: "Paperback su Amazon, stampato on demand. È disponibile anche in formato Kindle per chi preferisce leggere in digitale." },
  { q: "Come funziona la spedizione?", a: "Il libro è venduto e spedito direttamente da Amazon: se hai Prime, la spedizione è rapida e inclusa." },
  { q: "È adatto a chi parte da zero?", a: "Sì. È scritto proprio per chi ama il vino ma si sente perso davanti a una carta dei vini: niente paroloni, solo quello che serve davvero." },
  { q: "Che differenza c'è con il corso online?", a: "Il libro è un viaggio da leggere, regione per regione. I corsi sono percorsi guidati con lezioni, esercizi di degustazione e il mio supporto diretto. Si completano a vicenda." },
  { q: "Arriveranno altri volumi?", a: "Sì: 'Dentro il Calice' è una collana. In programma Francia, Spagna e Nuovo Mondo." },
];

function Faq({ items }) {
  const [open, setOpen] = useState(null);
  return (
    <div className="space-y-3" data-testid="faq-accordion">
      {items.map((f, i) => (
        <div key={i} className="rounded-xl border border-nero/10 bg-panna-dark/40 overflow-hidden">
          <button
            data-testid={`faq-item-${i}`}
            onClick={() => setOpen(open === i ? null : i)}
            className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
          >
            <span className="font-serif font-semibold text-nero">{f.q}</span>
            <ChevronDown size={18} className={`text-bordeaux transition-transform duration-300 shrink-0 ${open === i ? "rotate-180" : ""}`} />
          </button>
          <motion.div
            initial={false}
            animate={{ height: open === i ? "auto" : 0, opacity: open === i ? 1 : 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p className="px-6 pb-6 text-nero/75 text-sm leading-relaxed">{f.a}</p>
          </motion.div>
        </div>
      ))}
    </div>
  );
}

export default function IlLibro() {
  const [amazon, setAmazon] = useState("https://amzn.eu/d/01pW9tnS");
  const [bonus, setBonus] = useState({ email: "", amazon_order_id: "", privacy: false });
  const [bonusState, setBonusState] = useState({ loading: false, done: false, error: "" });
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    client.get("/settings/public").then((r) => setAmazon(r.data.amazon_url)).catch(() => {});
    client.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  const submitBonus = async (e) => {
    e.preventDefault();
    setBonusState({ loading: true, done: false, error: "" });
    if (!bonus.privacy) {
      setBonusState({ loading: false, done: false, error: "Devi accettare la Privacy Policy." });
      return;
    }
    try {
      await client.post("/bonus", bonus);
      trackEvent("bonus_webinar_request");
      setBonusState({ loading: false, done: true, error: "" });
    } catch (err) {
      setBonusState({ loading: false, done: false, error: formatApiError(err) });
    }
  };

  const AmazonBtn = ({ location, big = false, testId }) => (
    <a
      href={amazon}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackAmazonClick(location)}
      data-testid={testId}
      className={`group inline-flex items-center gap-3 rounded-full bg-bordeaux font-medium text-panna border border-oro/30 transition-colors duration-300 hover:bg-bordeaux-dark ${
        big ? "px-10 py-5 text-lg" : "px-8 py-4"
      }`}
    >
      <ShoppingCart size={big ? 20 : 18} />
      Compralo su Amazon
    </a>
  );

  return (
    <div data-testid="book-landing-page">
      <SEO title="Il Libro — Dentro il Calice – Italia" path="/il-libro"
           description="Dentro il Calice – Italia: il viaggio regione per regione nel vino italiano, raccontato da chi lo vende ogni giorno ai ristoranti. Paperback €14,99 su Amazon." />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center bg-nero grain overflow-hidden pt-24">
        <div className="absolute inset-0">
          <img src={IMGS.pour} alt="Vino versato in un calice con luce calda" className="h-full w-full object-cover opacity-25" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-nero via-nero/80 to-nero/40" />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 py-20 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                        className="text-oro text-xs uppercase tracking-[0.4em] mb-6">Il Libro</motion.p>
              <h1 className="font-serif font-bold text-panna text-4xl sm:text-5xl lg:text-6xl leading-[1.08]">
                <MaskedLines lines={["Dentro il Calice", "— Italia"]} delay={0.35} />
              </h1>
              <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
                        className="mt-7 text-panna/75 text-base md:text-lg leading-relaxed max-w-xl">
                Il viaggio regione per regione nel vino italiano, raccontato da chi lo vende ogni giorno
                ai ristoranti — non da un manuale.
              </motion.p>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8 }}
                          className="mt-8">
                <p className="font-serif text-3xl text-oro font-semibold">€14,99</p>
                <p className="text-panna/50 text-sm mt-1">Paperback · Disponibile su Amazon</p>
                <div className="mt-7">
                  <AmazonBtn location="hero" big testId="amazon-buy-button" />
                </div>
                <p className="mt-4 text-xs text-panna/45">Spedizione Prime · Stampa on demand · Anche in formato Kindle</p>
              </motion.div>
            </div>
            <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                        className="hidden sm:block py-10">
              <BookMockup />
            </motion.div>
          </div>
        </div>
      </section>

      {/* PER CHI È */}
      <section className="bg-panna py-24 sm:py-28" data-testid="section-per-chi">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="text-center mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-bordeaux">Per chi è questo libro</h2>
            <Divider className="mt-8" />
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { Icon: Wine, t: "Per chi ama il vino", d: "Ma si sente perso davanti a una carta dei vini troppo lunga e troppo seria." },
              { Icon: Users, t: "Per chi lavora nella ristorazione", d: "E vuole vendere meglio, consigliare meglio, raccontare meglio ogni bottiglia." },
              { Icon: MapPin, t: "Per chi vuole capire l'Italia del vino", d: "Regione per regione, vitigno per vitigno — senza paroloni." },
            ].map(({ Icon, t, d }, i) => (
              <Reveal key={t} delay={i * 0.12}>
                <div className="h-full rounded-2xl border border-nero/10 bg-panna-dark/40 p-8 text-center">
                  <div className="mx-auto h-14 w-14 rounded-full border-2 border-bordeaux/30 flex items-center justify-center mb-6">
                    <Icon size={22} className="text-bordeaux" />
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-nero leading-snug">{t}</h3>
                  <p className="mt-3 text-sm text-nero/70 leading-relaxed">{d}</p>
                  <p className="mt-5 text-oro">◆</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COSA TROVI DENTRO */}
      <section className="bg-nero py-24 sm:py-28 grain relative" data-testid="section-contenuti">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <Reveal>
                <p className="text-oro text-xs uppercase tracking-[0.35em] mb-4">L'indice, in sintesi</p>
                <h2 className="font-serif text-3xl sm:text-4xl font-bold text-panna">Cosa trovi dentro</h2>
              </Reveal>
              <Reveal delay={0.15}>
                <ul className="mt-8 space-y-4">
                  {CONTENTS.map((c) => (
                    <li key={c} className="flex items-start gap-3 text-panna/85">
                      <span className="text-oro mt-1 text-xs">◆</span>
                      <span className="leading-relaxed">{c}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <div className="grid grid-cols-2 gap-5" data-testid="book-gallery">
                {[
                  { src: "/assets/libro-cover.jpg", label: "Copertina" },
                  { src: "/assets/libro-retro.jpg", label: "Quarta di copertina" },
                ].map((g) => (
                  <figure key={g.label} className="group">
                    <div className="rounded-xl overflow-hidden border border-oro/15 shadow-[0_20px_45px_-15px_rgba(0,0,0,0.6)] bg-panna-dark/40 h-64 sm:h-[26rem] flex items-center justify-center">
                      <img src={g.src} alt={`${g.label} del libro Dentro il Calice — Italia`} loading="lazy"
                           className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-[1.03]" />
                    </div>
                    <figcaption className="text-center text-[0.7rem] uppercase tracking-[0.25em] text-oro/70 mt-3">{g.label}</figcaption>
                  </figure>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* AUTORE */}
      <section className="bg-panna py-24 sm:py-28" data-testid="section-autore">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-14 items-center">
            <Reveal>
              <img src={IMGS.portrait} alt="Stefano Cremonese, autore di Dentro il Calice – Italia"
                   className="rounded-2xl w-72 aspect-[4/5] object-cover shadow-xl mx-auto" loading="lazy" />
            </Reveal>
            <div>
              <Reveal>
                <p className="text-oliva text-xs uppercase tracking-[0.35em] mb-4">L'autore</p>
                <h2 className="font-serif text-3xl sm:text-4xl font-bold text-bordeaux">Stefano Cremonese</h2>
                <p className="mt-6 text-nero/80 leading-relaxed text-base md:text-lg font-serif italic">
                  "Non sono un esperto in cattedra. Sono uno che da trent'anni entra nelle cantine, parla con chi pota le viti
                  e poi porta quelle bottiglie nei ristoranti. Questo libro è quello che ho imparato strada facendo."
                </p>
                <p className="font-script text-4xl text-bordeaux mt-8">Stefano Cremonese</p>
                <Link to="/chi-sono" className="mt-6 inline-block text-bordeaux font-medium underline decoration-oro/50 underline-offset-4 hover:text-bordeaux-dark">
                  Leggi la mia storia completa
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ESTRATTO */}
      <section className="bg-nero py-24 grain relative" data-testid="section-estratto">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 text-center">
          <Reveal>
            <BookOpen size={26} className="text-oro mx-auto mb-6" />
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-panna">Sfoglia un estratto</h2>
            <p className="mt-5 text-panna/70 leading-relaxed">
              Vuoi assaggiare lo stile prima di comprare? Scarica la guida gratuita di 12 pagine:
              stesso tono, stessa strada.
            </p>
            <Link to="/#guida-gratuita" data-testid="cta-estratto"
                  className="mt-8 inline-flex items-center gap-2 rounded-full border border-oro/60 px-8 py-4 text-oro font-medium hover:bg-oro/10 transition-colors">
              Scarica l'assaggio gratuito
            </Link>
          </Reveal>
        </div>
      </section>

      {/* RECENSIONI — VOCE DEI CORSISTI */}
      {testimonials.length > 0 && (
        <section className="bg-panna py-24 sm:py-28" data-testid="section-recensioni">
          <div className="mx-auto max-w-6xl px-5 sm:px-8">
            <Reveal className="text-center mb-14">
              <p className="text-oliva text-xs uppercase tracking-[0.35em] mb-4">Le recensioni dei nostri corsi</p>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-bordeaux">La voce di chi ha seguito i corsi</h2>
              <Divider className="mt-8" />
            </Reveal>
            <TestimonialSlider testimonials={testimonials} />
          </div>
        </section>
      )}

      {/* BONUS */}
      <section className="bg-bordeaux py-24 grain relative" data-testid="section-bonus">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <Reveal className="text-center mb-10">
            <Gift size={26} className="text-oro mx-auto mb-5" />
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-panna">Bonus per chi compra</h2>
            <p className="mt-5 text-panna/80 leading-relaxed max-w-2xl mx-auto">
              Chi acquista il libro può richiedere gratis il webinar "Come leggere una carta dei vini".
              Lascia email e numero d'ordine Amazon: ti scriviamo noi.
            </p>
          </Reveal>
          {bonusState.done ? (
            <p data-testid="bonus-success" className="text-center font-serif text-xl text-oro">
              Richiesta ricevuta. Ti contatteremo presto con il link al webinar.
            </p>
          ) : (
            <form onSubmit={submitBonus} data-testid="bonus-form" className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
              <input type="email" required placeholder="La tua email" data-testid="bonus-email"
                     value={bonus.email} onChange={(e) => setBonus({ ...bonus, email: e.target.value })}
                     className="rounded-lg px-4 py-3.5 bg-nero/40 border border-panna/20 text-panna placeholder:text-panna/40 focus:outline-none focus:ring-2 focus:ring-oro/60" />
              <input type="text" required minLength={5} placeholder="Numero ordine Amazon" data-testid="bonus-order-id"
                     value={bonus.amazon_order_id} onChange={(e) => setBonus({ ...bonus, amazon_order_id: e.target.value })}
                     className="rounded-lg px-4 py-3.5 bg-nero/40 border border-panna/20 text-panna placeholder:text-panna/40 focus:outline-none focus:ring-2 focus:ring-oro/60" />
              <label className="sm:col-span-2 flex items-start gap-3 cursor-pointer">
                <input type="checkbox" data-testid="bonus-privacy" checked={bonus.privacy}
                       onChange={(e) => setBonus({ ...bonus, privacy: e.target.checked })} className="mt-1 h-4 w-4 accent-[#B0832F]" />
                <span className="text-xs text-panna/70 leading-relaxed">
                  Accetto la <a href="/privacy-policy" className="underline text-oro">Privacy Policy</a>.
                </span>
              </label>
              {bonusState.error && <p data-testid="bonus-error" className="sm:col-span-2 text-sm text-oro-light">{bonusState.error}</p>}
              <button type="submit" data-testid="bonus-submit" disabled={bonusState.loading}
                      className="sm:col-span-2 rounded-full bg-nero text-oro border border-oro px-8 py-4 font-medium hover:bg-nero-light transition-colors disabled:opacity-60">
                {bonusState.loading ? "Invio in corso…" : "Richiedi il webinar gratuito"}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-panna py-24 sm:py-28" data-testid="section-faq">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal className="text-center mb-14">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-bordeaux">Domande frequenti</h2>
            <Divider className="mt-8" />
          </Reveal>
          <Faq items={FAQS} />
        </div>
      </section>

      {/* CTA FINALE */}
      <section className="bg-nero py-28 grain relative" data-testid="section-cta-finale">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 text-center">
          <Reveal>
            <div className="mb-12"><BookMockup size="sm" /></div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-panna">
              L'Italia del vino, un calice alla volta.
            </h2>
            <p className="mt-4 font-serif text-2xl text-oro">€14,99 · Paperback</p>
            <div className="mt-8">
              <AmazonBtn location="final_cta" big testId="amazon-buy-button-final" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* STICKY BAR MOBILE */}
      <div data-testid="mobile-sticky-buy-bar"
           className="fixed bottom-0 inset-x-0 z-40 sm:hidden bg-nero/95 backdrop-blur border-t border-oro/25 px-4 py-3 flex items-center justify-between gap-3">
        <div>
          <p className="text-panna text-xs font-medium leading-tight">Dentro il Calice – Italia</p>
          <p className="text-oro font-serif font-semibold">€14,99</p>
        </div>
        <a href={amazon} target="_blank" rel="noopener noreferrer"
           onClick={() => trackAmazonClick("sticky_bar")}
           data-testid="sticky-amazon-button"
           className="rounded-full bg-bordeaux px-5 py-2.5 text-sm font-medium text-panna border border-oro/30">
          Compralo su Amazon
        </a>
      </div>
    </div>
  );
}
